import React from 'react';
import { X } from 'lucide-react';
import { Badge } from 'src/shared/components/ui/badge';
import { Button } from 'src/shared/components/ui/button';
import type { SelectedFilters } from 'src/app/portfolio/state/portfolioGeneralSlice';
import type { SiteFilterCountry, SiteFilterIsicCode } from 'src/app/portfolio/state/api/siteFilterOptionsQuery';
import { RISK_LEVELS, SON_LEVELS } from './RiskDimensionAccordion';

const FILTER_NAME_LABELS: Record<string, string> = {
    isic: 'Activity',
    country: 'Country',
    physical_risk_levels: 'Physical Risk',
    dependency_levels: 'Dependencies',
    impact_levels: 'Impact on Nature',
    son_levels: 'State of Nature',
    kba: 'KBA',
};

const KBA_VALUE_LABELS: Record<string, string> = {
    within_boundary: 'Within site boundary',
    within_buffer: 'Within site buffer zone',
    no_overlap: 'No overlap',
};

const RISK_VALUE_LABELS: Record<string, string> = Object.fromEntries([...RISK_LEVELS, ...SON_LEVELS].map(l => [l.value, l.label]));
const MAX_BADGE_LABEL_LENGTH = 25;

const truncateLabel = (value: string, maxLength = MAX_BADGE_LABEL_LENGTH) => {
    if (value.length <= maxLength) return value;
    return `${value.slice(0, maxLength - 1)}…`;
};

const resolveValueLabel = (name: string, value: string): string => {
    if (name === 'kba') return KBA_VALUE_LABELS[value] ?? value;
    return RISK_VALUE_LABELS[value] ?? value;
};

type Props = {
    selectedFilters: SelectedFilters;
    countries?: SiteFilterCountry[];
    isicCodes?: SiteFilterIsicCode[];
    onRemove: (name: string, values: string[]) => void;
    onClearAll: () => void;
    matchingSiteCount?: number;
};

const GnrActiveFilters = ({ selectedFilters, countries = [], isicCodes = [], onRemove, onClearAll, matchingSiteCount }: Props) => {
    const totalValues = selectedFilters.reduce((sum, f) => sum + f.values.length, 0);
    const countryNameByCode = new Map(countries.map(country => [country.code, country.name]));
    const isicLabelByCode = new Map(isicCodes.map(isicCode => [isicCode.code, `[${isicCode.code}] ${isicCode.description}`]));

    if (totalValues === 0) return null;

    return (
        <div className='flex flex-wrap items-center gap-2' data-testid='gnr-active-filters'>
            <span className='text-sm text-muted-foreground'>
                Active Filters
                {matchingSiteCount !== undefined
                    ? ` (${matchingSiteCount.toLocaleString()} ${matchingSiteCount === 1 ? 'site' : 'sites'})`
                    : ''}
                :
            </span>
            {selectedFilters.flatMap(filter =>
                filter.values.map(value => {
                    const nameLabel = FILTER_NAME_LABELS[filter.name] ?? filter.name;
                    const valueLabel =
                        filter.name === 'country'
                            ? countryNameByCode.get(value) ?? value
                            : filter.name === 'isic'
                            ? isicLabelByCode.get(value) ?? value
                            : resolveValueLabel(filter.name, value);
                    const fullBadgeLabel = `${nameLabel}: ${valueLabel}`;
                    return (
                        <Badge key={`${filter.name}-${value}`} variant='outline' className='flex items-center bg-white gap-1'>
                            <span title={fullBadgeLabel}>{truncateLabel(fullBadgeLabel)}</span>
                            <button
                                type='button'
                                aria-label={`Remove ${fullBadgeLabel} filter`}
                                onClick={() =>
                                    onRemove(
                                        filter.name,
                                        filter.values.filter(v => v !== value)
                                    )
                                }
                                className='inline-flex items-center justify-center text-muted-foreground hover:text-black'
                            >
                                <X className='h-3.5 w-3.5' />
                            </button>
                        </Badge>
                    );
                })
            )}
            {totalValues >= 2 && (
                <Button type='button' variant='link' size='sm' onClick={onClearAll} className='text-muted-foreground'>
                    Clear
                </Button>
            )}
        </div>
    );
};

export default GnrActiveFilters;
